import React, { useState } from 'react'
// @ts-ignore
import Select from 'react-select'
import { Select as MuiSelect, MenuItem, FormControl, TextField } from '@mui/material'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DesktopDatePicker, TimePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment, { Moment } from 'moment-timezone'
import { LocalizationContextType } from '../../shared/contexts'
import { temperatureUnit } from '../../../utils/formatTemperature'

interface CurrentFilters {
  sensorTitle?: string
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
  valueOperator?: string
  valueAmount?: string
}

interface SensorLogsFiltersProps {
  sensorTitles: string[]
  currentFilters: CurrentFilters
  localization_context?: LocalizationContextType
}

interface SelectOption {
  value: string
  label: string
}

const VALUE_OPERATORS = [
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'equals', label: 'Equals to' }
]

const SensorLogsFilters: React.FC<SensorLogsFiltersProps> = ({
  sensorTitles,
  currentFilters,
  localization_context: localizationContext
}) => {
  const [selectedSensor, setSelectedSensor] = useState<SelectOption | null>(
    currentFilters.sensorTitle
      ? { value: currentFilters.sensorTitle, label: currentFilters.sensorTitle }
      : null
  )
  const [startDate, setStartDate] = useState<Moment | null>(
    currentFilters.startDate ? moment(currentFilters.startDate) : null
  )
  const [endDate, setEndDate] = useState<Moment | null>(
    currentFilters.endDate ? moment(currentFilters.endDate) : null
  )
  const [startTime, setStartTime] = useState<Moment | null>(
    currentFilters.startTime ? moment(currentFilters.startTime, 'HH:mm') : null
  )
  const [endTime, setEndTime] = useState<Moment | null>(
    currentFilters.endTime ? moment(currentFilters.endTime, 'HH:mm') : null
  )
  const [valueOperator, setValueOperator] = useState<string>(currentFilters.valueOperator || 'equals')
  const [valueAmount, setValueAmount] = useState<string>(currentFilters.valueAmount || '')

  const sensorOptions: SelectOption[] = sensorTitles.map(title => ({
    value: title,
    label: title
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const params = new URLSearchParams()

    if (selectedSensor) params.append('sensor_title', selectedSensor.value)
    if (startDate) params.append('start_date', startDate.format('YYYY-MM-DD'))
    if (endDate) params.append('end_date', endDate.format('YYYY-MM-DD'))
    if (startTime) params.append('start_time', startTime.format('HH:mm'))
    if (endTime) params.append('end_time', endTime.format('HH:mm'))
    if (valueOperator && valueAmount) {
      params.append('value_operator', valueOperator)
      params.append('value_amount', valueAmount)
    }

    window.location.href = `/admin/sensor_logs?${params.toString()}`
  }

  const handleClear = () => {
    window.location.href = '/admin/sensor_logs'
  }

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '38px',
      borderColor: '#dee2e6'
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#757575',
      padding: '8px',
      ':hover': {
        color: '#424242'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  }

  return (
    <div className="card mb-3">
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Sensor</label>
              <Select
                isClearable
                options={sensorOptions}
                value={selectedSensor}
                onChange={(selected: SelectOption | null) => setSelectedSensor(selected)}
                placeholder="Select sensor..."
                styles={selectStyles}
                className="basic-select"
                classNamePrefix="select"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Sensor Value ({temperatureUnit(localizationContext)})</label>
              <div className="d-flex gap-2">
                <FormControl size="small" style={{ minWidth: '140px' }}>
                  <MuiSelect
                    value={valueOperator}
                    onChange={(e) => setValueOperator(e.target.value)}
                    IconComponent={() => (
                      <i className="fa fa-chevron-down" style={{ color: '#757575', marginRight: '12px', fontWeight: 300, fontSize: '12px' }}/>
                    )}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#dee2e6'
                      },
                      '& .MuiSelect-icon': {
                        top: 'calc(50% - 5px)',
                        right: '8px'
                      }
                    }}
                  >
                    {VALUE_OPERATORS.map(op => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Value"
                  value={valueAmount}
                  onChange={(e) => setValueAmount(e.target.value)}
                  inputProps={{ step: '0.1' }}
                  sx={{
                    width: '120px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <div className="row g-3 mt-2">
            <div className="col-md-3">
              <label className="form-label">Date From</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <DesktopDatePicker
                  className="w-100"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="col-md-3">
              <label className="form-label">Date To</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <DesktopDatePicker
                  className="w-100"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="col-md-3">
              <label className="form-label">Time From</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <TimePicker
                  className="w-100"
                  value={startTime}
                  onChange={(time) => setStartTime(time)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="col-md-3">
              <label className="form-label">Time To</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <TimePicker
                  className="w-100"
                  value={endTime}
                  onChange={(time) => setEndTime(time)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>

          <div className="d-flex gap-2 mt-3 justify-content-end">
            <button type="submit" className="btn btn-primary">
              Search
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SensorLogsFilters
